import { parse } from 'date-fns'
import { Training, TrainingType } from 'session-info/training'
import { TimeZoneNames, TrainingDTO } from './model'

/** Null is how the backend stores ONLINE, as `Session` reads it. */
const trainingSessionType = (
	dto: Pick<TrainingDTO, 'sessionType'>
): TrainingType => dto.sessionType ?? 'ONLINE'

/**
 * Which chunk's type the room will actually be, mirroring the backend's
 * `Session.getNextChunkSessionType()`: a chunk that names its own type wins,
 * and only when none does is the training-level type used.
 *
 * This matters for exactly one thing today, and it is not cosmetic. The entry
 * gate chooses between a device check and the in-person screen, and a blended
 * training whose live chunk is IN_PERSON would otherwise show the online gate
 * and ask every laptop in a physical room for a camera - the one thing the
 * feature promises never happens.
 */
export const currentChunkSessionType = (
	dto: Pick<TrainingDTO, 'sessionType' | 'script'>
): TrainingType => {
	// In start order: the backend refuses a script whose chunks are not
	// (`SessionScript.validateConsecutiveChunks`). Once every chunk is over,
	// the last one stands for the training, as session-planning's
	// `currentChunk` has it - the two apps then agree on every training.
	const chunks = dto.script?.sessionChunks ?? []
	const current =
		chunks.find(chunk => chunk.state === 'LIVE') ??
		chunks.find(chunk => chunk.state !== 'FINISHED') ??
		chunks[chunks.length - 1]

	return current?.sessionType ?? trainingSessionType(dto)
}

export const dtoToTraining = (dto: TrainingDTO): Training => {
	return {
		...dto,
		id: dto.id,
		subject: dto.name,
		startDate: parse(dto.startDate, 'yyyy-MM-dd', new Date()),
		timezone: dto.timezone as TimeZoneNames,
		organizationId: dto.organizationId,
		sessionType: trainingSessionType(dto),
		currentChunkSessionType: currentChunkSessionType(dto),
		venue: dto.venue ?? undefined,
	}
}
